/**
 * @rtvloco/theme — tokens.ts
 *
 * Concrete resolved token values for light and dark themes.
 *
 * Rules:
 * - NO CSS var(--...) strings. Every value is a concrete hex/rgba/number.
 * - Values MUST stay in sync with light.css and dark.css in this package.
 * - MapLibre layers MUST read from this module — never from getComputedStyle.
 * - Add new tokens here AND to the corresponding CSS file before shipping.
 */
// ── Concrete token tables ─────────────────────────────────────────────────
// Values mirror light.css and dark.css. Update both when changing a token.
const LIGHT_TOKENS = {
    // Signal — mirrors --signal-* in light.css
    signalNominal: '#15803d',
    signalDegraded: '#b45309',
    signalIncident: '#dc2626',
    signalInfo: '#0891b2',
    signalInfoStrong: '#0e6f87',
    signalForecast: '#7c3aed',
    signalNominalBg: 'rgba(21,  128, 61,  0.08)',
    signalDegradedBg: 'rgba(180, 83,  9,   0.08)',
    signalIncidentBg: 'rgba(220, 38,  38,  0.08)',
    signalInfoBg: 'rgba(8,   145, 178, 0.08)',
    signalForecastBg: 'rgba(124, 58,  237, 0.08)',
    dataSeries: [
        '#2563eb', '#7c3aed', '#0891b2', '#15803d',
        '#b45309', '#c2410c', '#be185d', '#475569',
    ],
    // Surface — mirrors --color-* in light.css
    colorBg: '#f1f5f9',
    colorSurface: '#ffffff',
    colorSurfaceRaised: '#f8fafc',
    colorText: '#0f172a',
    colorTextSecondary: '#475569',
    colorMuted: '#5a6673',
    colorDisabled: '#cbd5e1',
    colorBorder: '#e2e8f0',
    colorBorderStrong: '#cbd5e1',
    // Semantic
    colorSuccess: '#16a34a',
    colorSuccessLight: 'rgba(22, 163, 74, 0.08)',
    colorWarning: '#d97706',
    colorWarningLight: 'rgba(217, 119, 6, 0.08)',
    colorError: '#dc2626',
    colorErrorLight: 'rgba(220, 38, 38, 0.08)',
    // Map vehicle — brighter than signal-nominal for readability on basemap
    mapVehicleOnTime: '#16a34a', // brighter map variant; DOM signal uses AA-adjusted #15803d
    mapVehicleEarly: '#3b82f6', // blue 500 — "ahead of schedule" (design convention)
    mapVehicleLate: '#d97706', // = signalDegraded light
    mapVehicleOutdated: '#64748b', // map-specific slate for basemap readability
    mapVehicleOffline: '#94a3b8', // slate 400 — lighter than muted
    mapVehicleEmergency: '#dc2626', // = signalIncident light
    // Map feature-state — brand-fixed, same in both modes
    mapVehicleSelected: '#2563eb', // blue 600
    mapVehicleFocused: '#a855f7', // purple 500
    mapDepotSelected: '#00ffff', // cyan — Contract v1.1
    // Basemap — lighter feel in light mode
    mapRasterSaturation: -0.15,
    mapRasterBrightnessMin: 0.35,
    mapRasterBrightnessMax: 1.00,
    mapRasterContrast: 0.10,
};
const DARK_TOKENS = {
    // Signal — mirrors --signal-* in dark.css
    signalNominal: '#22c55e',
    signalDegraded: '#f59e0b',
    signalIncident: '#ef4444',
    signalInfo: '#0891b2',
    signalInfoStrong: '#67e8f9',
    signalForecast: '#a78bfa',
    signalNominalBg: 'rgba(34,  197, 94,  0.12)',
    signalDegradedBg: 'rgba(245, 158, 11,  0.12)',
    signalIncidentBg: 'rgba(239, 68,  68,  0.12)',
    signalInfoBg: 'rgba(8,   145, 178, 0.18)',
    signalForecastBg: 'rgba(167, 139, 250, 0.16)',
    dataSeries: [
        '#60a5fa', '#a78bfa', '#22d3ee', '#4ade80',
        '#fbbf24', '#fb923c', '#f472b6', '#a8a29e',
    ],
    // Surface — mirrors --color-* in dark.css
    colorBg: '#0c0a09',
    colorSurface: '#1c1917',
    colorSurfaceRaised: '#292524',
    colorText: '#f5f5f4',
    colorTextSecondary: '#a8a29e',
    colorMuted: '#9e9892',
    colorDisabled: '#44403c',
    colorBorder: '#292524',
    colorBorderStrong: '#44403c',
    // Semantic
    colorSuccess: '#22c55e',
    colorSuccessLight: 'rgba(34, 197, 94, 0.12)',
    colorWarning: '#f59e0b',
    colorWarningLight: 'rgba(245, 158, 11, 0.12)',
    colorError: '#ef4444',
    colorErrorLight: 'rgba(239, 68, 68, 0.12)',
    // Map vehicle — brighter for dark basemap readability
    mapVehicleOnTime: '#22c55e', // = signalNominal dark
    mapVehicleEarly: '#3b82f6', // blue 500 — same by convention
    mapVehicleLate: '#f59e0b', // = signalDegraded dark
    mapVehicleOutdated: '#78716c', // map-specific stone for basemap readability
    mapVehicleOffline: '#9ca3af', // gray 400
    mapVehicleEmergency: '#ef4444', // = signalIncident dark
    // Map feature-state — brand-fixed, same in both modes
    mapVehicleSelected: '#2563eb',
    mapVehicleFocused: '#a855f7',
    mapDepotSelected: '#00ffff',
    // Basemap — darker, more desaturated for dark mode
    mapRasterSaturation: -0.50,
    mapRasterBrightnessMin: 0.10,
    mapRasterBrightnessMax: 0.85,
    mapRasterContrast: 0.20,
};
/** Concrete contrast outputs for canvas/SVG algorithms that cannot consume CSS variables. */
export const contrastTextTokens = {
    onLightBackground: '#000000',
    onDarkBackground: '#ffffff',
};
/** Governed bitmap-editor swatches shared by image-authoring surfaces. */
export const bitmapEditorColorPalette = [
    '#000000', '#ffffff', '#ff0000', '#00ff00', '#0000ff',
    '#ffff00', '#ff00ff', '#00ffff', '#808080', '#c0c0c0',
    '#800000', '#008000', '#000080', '#808000', '#800080',
    '#008080', '#ff8000', '#ff0080', '#80ff00', '#0080ff',
];
/**
 * Every concrete colour custom-property value shipped by base.css plus the
 * selected theme stylesheet. The computed-style audit consumes this palette;
 * keeping it here makes tokens.ts the authority for both JS and CSS clients.
 * Shadows are intentionally excluded because the audit does not classify
 * box-shadow values.
 */
const SHARED_CANONICAL_COLORS = [
    '#ffffff', '#0b0d0f', '#ea580c', '#2563eb',
    '#e8a030', '#5a9eff', '#b58cff', '#ff7a40', '#34c58a', '#f5564e',
    '#6ba0f5', '#16a371', '#f2f3f5',
];
export const canonicalColorTokens = {
    light: [
        ...SHARED_CANONICAL_COLORS,
        '#f1f5f9', '#ffffff', '#f8fafc', '#0f172a', '#475569', '#5a6673',
        '#cbd5e1', '#e2e8f0', '#c2410c', 'rgba(234, 88, 12, 0.08)',
        '#16a34a', 'rgba(22, 163, 74, 0.08)', '#d97706',
        'rgba(217, 119, 6, 0.08)', '#dc2626', 'rgba(220, 38, 38, 0.08)',
        '#1c1917', '#e7e5e4', '#a8a29e', '#fdba74',
        'rgba(234, 88, 12, 0.20)', 'rgba(255, 255, 255, 0.08)',
        'rgba(148, 163, 184, 0.15)', '#f5f6f8', '#e2e5ea', '#ebedf0',
        'rgba(0, 0, 0, 0.02)', 'rgba(0, 0, 0, 0.04)', '#eef1f4',
        '#15803d', '#b45309', '#0891b2', '#7c3aed',
        'rgba(21, 128, 61, 0.08)', 'rgba(180, 83, 9, 0.08)',
        'rgba(8, 145, 178, 0.08)', 'rgba(124, 58, 237, 0.08)',
        '#f87171', '#991b1b', '#92400e', '#0e6f87', '#fee2e2', '#fef3c7',
        '#166534', '#d1fae5', '#3b82f6', '#eff6ff', '#dbeafe', '#1e293b',
        '#be185d',
        '#1e2028', '#eef0f2', 'rgba(0, 0, 0, 0.04)',
        'rgba(13, 18, 26, 0.35)',
    ],
    dark: [
        ...SHARED_CANONICAL_COLORS,
        '#0c0a09', '#1c1917', '#292524', '#f5f5f4', '#a8a29e', '#9e9892',
        '#44403c', '#fb923c', '#f97316', 'rgba(234, 88, 12, 0.15)',
        '#22c55e', 'rgba(34, 197, 94, 0.12)', '#f59e0b',
        'rgba(245, 158, 11, 0.12)', '#ef4444', 'rgba(239, 68, 68, 0.12)',
        '#d6d3d1', '#fdba74', 'rgba(234, 88, 12, 0.20)',
        'rgba(255, 255, 255, 0.06)', 'rgba(71, 85, 105, 0.4)', '#161412',
        'rgba(255, 255, 255, 0.02)', 'rgba(255, 255, 255, 0.04)', '#3a3633',
        '#0891b2', '#a78bfa', 'rgba(8, 145, 178, 0.18)',
        'rgba(167, 139, 250, 0.16)', '#fca5a5', '#fbbf24', '#67e8f9',
        'rgba(239, 68, 68, 0.08)', 'rgba(245, 158, 11, 0.08)', '#4ade80',
        'rgba(34, 197, 94, 0.08)', '#60a5fa', '#3b82f6',
        'rgba(96, 165, 250, 0.08)', 'rgba(96, 165, 250, 0.15)', '#e2e8f0',
        '#22d3ee', '#f472b6',
        '#0d0f14', '#1e242a', '#181d22', 'rgba(255, 255, 255, 0.05)',
        'rgba(0, 0, 0, 0.5)',
    ],
};
export function getThemeTokens(theme) {
    return theme === 'dark' ? DARK_TOKENS : LIGHT_TOKENS;
}
// Mirrors base.css :root typography block.
export const typography = {
    fontFamily: "'Inter Variable', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontFamilyMono: "'JetBrains Mono Variable', 'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
    fontSize: {
        xs: '0.6875rem',
        sm: '0.75rem',
        md: '0.875rem',
        lg: '1rem',
        xl: '1.125rem',
        xxl: '1.5rem',
        xxxl: '1.875rem',
    },
    fontWeight: {
        regular: 400,
        medium: 500,
        semibold: 600,
        bold: 700,
    },
    lineHeight: {
        tight: 1.2,
        normal: 1.5,
        relaxed: 1.75,
    },
    letterSpacing: {
        tight: '-0.01em',
        normal: '0',
        wide: '0.025em',
        caption: '0.08em',
        captionLegacy: '0.05em',
    },
    // Mirrors the base.css element defaults + .text-* utilities (NEXT-513).
    scale: {
        display: { fontSize: '1.875rem', fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.01em' },
        h1: { fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.25, letterSpacing: '-0.01em' },
        h2: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.3, letterSpacing: '0' },
        h3: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.35, letterSpacing: '0' },
        h4: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.4, letterSpacing: '0' },
        h5: { fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.5, letterSpacing: '0' },
        h6: { fontSize: '0.6875rem', fontWeight: 600, lineHeight: 1.5, letterSpacing: '0.025em' },
        body: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.5, letterSpacing: '0' },
        bodySm: { fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.5, letterSpacing: '0' },
        caption: { fontSize: '0.6875rem', fontWeight: 400, lineHeight: 1.5, letterSpacing: '0.025em' },
    },
};
export function getTypographyTokens() {
    return typography;
}
//# sourceMappingURL=tokens.js.map